/**
 * CreateFormTemplateDialog — création atomique template + brouillon v1 (1c-i).
 *
 * Champs MVP : code, nom, description?, langue par défaut, tags?. Le client
 * n'envoie JAMAIS status / version_number / published_at / tenant / created_by
 * (posés serveur). Succès → callback `onCreated(result)` (le parent navigue vers
 * le détail + toast « brouillon v1 créé »). Code dupliqué → erreur inline sur le
 * champ code. Double-submit bloqué. Focus initial sur le code ; Escape/backdrop
 * ferment (sauf pendant l'envoi) ; le parent restaure le focus au déclencheur.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { TagsInput } from "../../../components/TagsInput";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  type CreateFormTemplateResult,
  useCreateFormTemplate,
} from "../../../lib/queries/formTemplates";

type Props = {
  readonly onClose: () => void;
  readonly onCreated: (result: CreateFormTemplateResult) => void;
};

export function CreateFormTemplateDialog({ onClose, onCreated }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const codeId = useId();
  const nameId = useId();
  const descId = useId();
  const langId = useId();
  const tagsId = useId();
  const codeInputRef = useRef<HTMLInputElement | null>(null);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState<"fr" | "en">("fr");
  const [tags, setTags] = useState<string[]>([]);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const create = useCreateFormTemplate();
  const { reset: resetMutation } = create;

  useEffect(() => {
    resetMutation();
    queueMicrotask(() => codeInputRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !create.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, create.isPending]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (create.isPending) return;

    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    let invalid = false;
    if (trimmedCode === "") {
      setCodeError(t("form_templates.create.errors.code_required"));
      invalid = true;
    }
    if (trimmedName === "") {
      setNameError(t("form_templates.create.errors.name_required"));
      invalid = true;
    }
    if (invalid) return;

    create.mutate(
      {
        code: trimmedCode,
        name: trimmedName,
        ...(description.trim() ? { description: description.trim() } : {}),
        default_language: defaultLanguage,
        ...(tags.length ? { tags } : {}),
      },
      {
        onSuccess: (result) => onCreated(result),
        onError: (err) => {
          if (
            err instanceof ApiException &&
            err.status === 409 &&
            err.apiError.code === "FORM_TEMPLATE_CODE_DUPLICATE"
          ) {
            setCodeError(t("form_templates.create.errors.code_duplicate"));
            queueMicrotask(() => codeInputRef.current?.focus());
          }
        },
      },
    );
  };

  // Erreur réseau/serveur générique (hors code dupliqué déjà rendu inline).
  const isDuplicate =
    create.error instanceof ApiException &&
    create.error.apiError.code === "FORM_TEMPLATE_CODE_DUPLICATE";
  const genericError =
    create.isError && !isDuplicate
      ? create.error instanceof ApiException
        ? getApiErrorMessage(create.error, lang) || t("errors.generic")
        : t("errors.generic")
      : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!create.isPending) onClose();
        }}
      />
      <form
        onSubmit={onSubmit}
        className="motion-safe:animate-fade-in relative z-10 w-full max-w-lg rounded-xl bg-white shadow-xl ring-1 ring-slate-200"
      >
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("form_templates.create.title")}
          </h2>
          <p className="mt-1 text-sm text-slate-600">{t("form_templates.create.subtitle")}</p>
        </header>

        <div className="space-y-4 px-5 py-5">
          {genericError && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {genericError}
            </div>
          )}

          {/* Code */}
          <div>
            <label htmlFor={codeId} className="block text-sm font-medium text-slate-700">
              {t("form_templates.fields.code")}
            </label>
            <input
              ref={codeInputRef}
              id={codeId}
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (codeError) setCodeError(null);
              }}
              autoComplete="off"
              spellCheck={false}
              maxLength={200}
              aria-invalid={codeError ? true : undefined}
              aria-describedby={codeError ? `${codeId}-error` : `${codeId}-hint`}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline-none focus-visible:ring-2"
            />
            {codeError ? (
              <p id={`${codeId}-error`} role="alert" className="mt-1 text-xs text-red-600">
                {codeError}
              </p>
            ) : (
              <p id={`${codeId}-hint`} className="mt-1 text-xs text-slate-500">
                {t("form_templates.fields.code_hint")}
              </p>
            )}
          </div>

          {/* Nom */}
          <div>
            <label htmlFor={nameId} className="block text-sm font-medium text-slate-700">
              {t("form_templates.fields.name")}
            </label>
            <input
              id={nameId}
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              maxLength={300}
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? `${nameId}-error` : undefined}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline-none focus-visible:ring-2"
            />
            {nameError && (
              <p id={`${nameId}-error`} role="alert" className="mt-1 text-xs text-red-600">
                {nameError}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor={descId} className="block text-sm font-medium text-slate-700">
              {t("form_templates.fields.description")}
              <span className="ml-1 font-normal text-slate-400">
                {t("form_templates.fields.optional")}
              </span>
            </label>
            <textarea
              id={descId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={2000}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>

          {/* Langue par défaut */}
          <div>
            <label htmlFor={langId} className="block text-sm font-medium text-slate-700">
              {t("form_templates.fields.default_language")}
            </label>
            <select
              id={langId}
              value={defaultLanguage}
              onChange={(e) => setDefaultLanguage(e.target.value as "fr" | "en")}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline-none focus-visible:ring-2"
            >
              <option value="fr">{t("form_templates.language.fr")}</option>
              <option value="en">{t("form_templates.language.en")}</option>
            </select>
          </div>

          {/* Tags */}
          <div>
            <label htmlFor={tagsId} className="block text-sm font-medium text-slate-700">
              {t("form_templates.fields.tags")}
              <span className="ml-1 font-normal text-slate-400">
                {t("form_templates.fields.optional")}
              </span>
            </label>
            <div className="mt-1">
              <TagsInput
                id={tagsId}
                value={tags}
                onChange={setTags}
                placeholder={t("form_templates.fields.tags_placeholder")}
              />
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={create.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            aria-busy={create.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {create.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {create.isPending
              ? t("form_templates.create.submitting")
              : t("form_templates.create.submit")}
          </button>
        </footer>
      </form>
    </div>
  );
}
